import { BaseSceneData, BaseSceneTransitionConfig, TransitionSceneBaseData } from '@scarybeasties/phaser-framework-core/scenes';
import { TransitionSettings } from '@scarybeasties/phaser-framework-core/settings';
import { SceneAssets } from '@scarybeasties/phaser-framework-core/modules';
import { App } from './App';
import { RunSettings } from './game/RunSettingsHandler';

import { assetPackLookup, AssetPack, SpineAssets, audioLookup, buildSpineLookup } from './assets-to-load';

export const SceneId = {
  Boot: 'Boot',
  PreloadGeneric: 'PreloadGeneric',
  Title: 'Title',
  LevelSelect: 'LevelSelect',
  CharacterSelect: 'CharacterSelect',
  Game: 'Game',
  GameUI: 'GameUI',
  TransitionSceneToScene: 'TransitionSceneToScene',
  TransitionLoadScene: 'TransitionLoadScene',
  DebugUI: 'DebugUI'
} as const;

export const TransitionPoint = {
  BootEnd: 'BootEnd',
  PreloadEnd: 'PreloadEnd',
  TitleEnd: 'TitleEnd',
  LevelSelectBack: 'LevelSelectBack',
  LevelSelectEnd: 'LevelSelectEnd',
  CharacterSelectBack: 'CharacterSelectBack',
  CharacterSelectEnd: 'CharacterSelectEnd',
  GameBack: 'GameBack',
  GameHome: 'GameHome',
  GameLevelSelect: 'GameLevelSelect',
  GameReplay: 'GameReplay'
} as const;

export const scenes = [
  SceneId.Boot,
  SceneId.PreloadGeneric,
  SceneId.Title,
  SceneId.LevelSelect,
  SceneId.CharacterSelect,
  SceneId.Game,
  SceneId.GameUI,
  SceneId.TransitionSceneToScene,
  SceneId.TransitionLoadScene,
  SceneId.DebugUI
];

// Scene Asset Dependencies.
const baseAssetPacks: Set<string> = new Set([AssetPack.Common, SpineAssets.Taz]);
const sceneAssetData: Map<keyof typeof SceneId, SceneAssets> = new Map();
sceneAssetData.set(SceneId.Title, { requiredAssetPacks: [AssetPack.Title], requiredSpineAssets:[ SpineAssets.TitleFireworks,SpineAssets.Taz] });
sceneAssetData.set(SceneId.LevelSelect, { requiredAssetPacks: [AssetPack.LevelSelect] });
sceneAssetData.set(SceneId.CharacterSelect, { requiredAssetPacks: [AssetPack.CharacterSelect] });
sceneAssetData.set(SceneId.Game, { hasDynamicAssets: true, requiredAssetPacks: [AssetPack.Game] });

// Routing table.
const sceneMap: Map<keyof typeof TransitionPoint, keyof typeof SceneId | TransitionSettings> = new Map();
sceneMap.set(TransitionPoint.BootEnd, SceneId.PreloadGeneric);
sceneMap.set(TransitionPoint.PreloadEnd, SceneId.Title);
sceneMap.set(TransitionPoint.TitleEnd, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.LevelSelectBack, {
  nextScene: SceneId.Title,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.LevelSelectEnd, {
  nextScene: SceneId.CharacterSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.CharacterSelectBack, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.CharacterSelectEnd, {
  nextScene: SceneId.Game,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameBack, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameHome, {
  nextScene: SceneId.Title,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameLevelSelect, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameReplay, {
  nextScene: SceneId.Game,
  transitionScene: SceneId.TransitionSceneToScene,
  transitionTime: 1000,
  waitForPreload: true
});

export const setupRouting = (app: App) => {
  app.updateSceneAssetMaps(assetPackLookup, buildSpineLookup(app.Config.spineBasePath), audioLookup, baseAssetPacks, sceneAssetData);
  app.updateSceneMap(sceneMap as unknown as Map<string, string>, TransitionPoint.PreloadEnd.toString());
};

export interface CustomSceneTransitionConfig extends BaseSceneTransitionConfig {
  data: SceneData;
}

export interface SceneData extends BaseSceneData {
  sourceScene: keyof typeof SceneId;
  sourceTransition?: keyof typeof TransitionPoint;
  transitionData?: TransitionSceneData;
  gameSceneData?: GameSceneData;
  runSettings?: Partial<RunSettings>;
  [key: string]: any;
}

export interface TransitionSceneData extends TransitionSceneBaseData {}

export interface GameSceneData {
  shouldSkip: boolean;
}
